import { AuthorizedInstallerDialog } from '@authorizedInstaller/components/display/dialogs';
import { AUTHORIZED_INSTALLER_TABS, AuthorizedInstallersDashboardContext } from '@authorizedInstaller/contexts';
import {
    useAcceptAuthorizedInstallerRequestMutation,
    useGetAuthorizedInstallerRequestsQuery,
    useRejectAuthorizedInstallerRequestMutation,
} from '@authorizedInstaller/services';
import { IAuthorizedInstallerRequest } from '@authorizedInstaller/types';
import { TableHeader } from '@common/types/common';
import { LoadingContainer } from '@components/display/loading/LoadingContainer';
import { Table } from '@components/display/tables';
import { HoldButton } from '@components/utility/buttons';
import { Search } from '@components/utility/inputs/Search';
import { useAppDispatch } from '@hooks/generic';
import { usePagination } from '@hooks/usePagination';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Card, Chip, CircularProgress, Divider, Tab, TableCell, Tabs, Typography } from '@mui/material';
import { api } from '@services/api';
import { setTitle } from '@store/appSlice';
import { handleError, toaster } from '@utils/common';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const TABLE_HEADERS: TableHeader<Partial<IAuthorizedInstallerRequest>>[] = [
    {
        id: 'user',
        label: 'FULL NAME',
    },
    {
        id: 'id',
        label: 'COMPANY NAME',
        disabled: true,
    },
    {
        id: 'distributor',
        label: 'DISTRIBUTOR',
    },
    {
        id: 'certificate_files',
        label: 'CERTIFICATES UPLOADED',
        disabled: true,
    },
    {
        id: 'installation_files',
        label: 'INSTALLATIONS UPLOADED',
        disabled: true,
    },
];

const rowsPerPageOptions = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const FormsOverview = () => {
    const [rowStates, setRowStates] = useState<Record<string, 'loading' | 'declined' | 'accepted'>>({});
    const [selectedInstaller, setSelectedInstaller] = useState<IAuthorizedInstallerRequest | null>(null);
    const {
        handleParamChange,
        sort,
        filters,
        search,
        setSearch,
        handleSortClick,
        handlePageChange,
        handlePageSizeChange,
    } = usePagination<IAuthorizedInstallerRequest>({ defaultSort: 'user', allowedColumns: ['user', 'distributor'] });
    const [acceptRequest] = useAcceptAuthorizedInstallerRequestMutation();
    const [rejectRequest] = useRejectAuthorizedInstallerRequestMutation();

    const [searchParams] = useSearchParams();
    const tab = useMemo(() => {
        const currentTab = searchParams.get('tab') ?? 0;
        return currentTab in AUTHORIZED_INSTALLER_TABS
            ? (Number(currentTab) as AUTHORIZED_INSTALLER_TABS)
            : AUTHORIZED_INSTALLER_TABS.NEW_FORMS;
    }, [searchParams]);
    const dispatch = useAppDispatch();

    const { data: newForms } = useGetAuthorizedInstallerRequestsQuery({
        filters: {
            status: ['pending'],
        },
        page: 1,
        page_size: Number(filters.page_size) || 10,
    });

    const { data, isFetching } = useGetAuthorizedInstallerRequestsQuery({
        page_size: Number(filters.page_size) || 10,
        filters: {
            search: [filters.search],
            status: tab === AUTHORIZED_INSTALLER_TABS.OLD_FORMS ? ['accepted', 'rejected'] : ['pending'],
        },
        page: Number(filters.page) || 1,
        ordering: filters.sort,
    });

    useEffect(() => {
        dispatch(setTitle('Extended Warranty Form'));
    }, [dispatch]);

    const renderRow = useCallback(
        (item: Partial<IAuthorizedInstallerRequest>) => (
            <>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                        className="truncate"
                    >
                        {item.user?.first_name} {item.user?.last_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                        className="truncate"
                    >
                        {item.user?.company_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                        className="truncate"
                    >
                        {item.distributor?.company_name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                    >
                        {item.certificate_files?.length}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography
                        fontSize={{
                            xs: 10,
                            sm: 14,
                        }}
                    >
                        {item.installation_files?.length}
                    </Typography>
                </TableCell>
            </>
        ),
        []
    );

    const handleAccept = useCallback(
        async (item: Partial<IAuthorizedInstallerRequest>) => {
            try {
                if (!item.id) throw 'No ID associated';
                setRowStates((prev) => ({ ...prev, [item.id as number]: 'loading' }));
                await acceptRequest(item.id).unwrap();
                setRowStates((prev) => ({ ...prev, [item.id as number]: 'accepted' }));
                toaster.success({ msg: 'Application Form Was Approved', title: 'ACCEPTED' });

                setTimeout(() => {
                    dispatch(api.util.invalidateTags(['AuthorizedInstallerRequests']));
                }, 2000);
            } catch (err) {
                handleError('Error accepting request', err);
                const newRowStates = { ...rowStates };
                delete rowStates[item.id as number];
                setRowStates(newRowStates);
            }
        },
        [acceptRequest, dispatch, rowStates]
    );

    const handleDecline = useCallback(
        async (item: Partial<IAuthorizedInstallerRequest>) => {
            try {
                if (!item.id) throw 'No ID associated';
                setRowStates((prev) => ({ ...prev, [item.id as number]: 'loading' }));
                await rejectRequest(item.id).unwrap();
                setRowStates((prev) => ({ ...prev, [item.id as number]: 'declined' }));
                toaster.failure({ msg: 'Application Form Was Declined', title: 'DECLINED' });

                setTimeout(() => {
                    dispatch(api.util.invalidateTags(['AuthorizedInstallerRequests']));
                }, 2000);
            } catch (err) {
                handleError('Error rejecting request', err);
                const newRowStates = { ...rowStates };
                delete rowStates[item.id as number];
                setRowStates(newRowStates);
            }
        },
        [dispatch, rejectRequest, rowStates]
    );

    const getRowOptions = useCallback(
        (item?: Partial<IAuthorizedInstallerRequest>) =>
            item?.id ? (
                <div
                    className={clsx('flex gap-2 bg-inherit', {
                        'justify-center': tab === AUTHORIZED_INSTALLER_TABS.NEW_FORMS,
                        'justify-center xl:justify-start': tab === AUTHORIZED_INSTALLER_TABS.OLD_FORMS,
                    })}
                >
                    {tab === AUTHORIZED_INSTALLER_TABS.NEW_FORMS ? (
                        !rowStates[item.id] ? (
                            <>
                                <HoldButton
                                    color="error"
                                    className="w-[66px] h-[30px] rounded"
                                    holdTime={1000}
                                    disableElevation={false}
                                    holdBackgrondPalleteKey="error-300"
                                    onHoldComplete={() => handleDecline(item)}
                                >
                                    <Typography fontFamily="Roboto" fontWeight={500} variant="body2" lineHeight="22px">
                                        DECLINE
                                    </Typography>
                                </HoldButton>
                                <HoldButton
                                    className="w-[66px] h-[30px] rounded"
                                    holdTime={1000}
                                    disableElevation={false}
                                    holdBackgrondPalleteKey="primary-300"
                                    onHoldComplete={() => handleAccept(item)}
                                >
                                    <Typography fontFamily="Roboto" fontWeight={500} variant="body2" lineHeight="22px">
                                        ACCEPT
                                    </Typography>
                                </HoldButton>
                            </>
                        ) : rowStates[item.id] === 'loading' ? (
                            <CircularProgress size={20} />
                        ) : (
                            <Card
                                className={clsx('w-full mx-3 h-[30px] flex items-center justify-center gap-1', {
                                    'bg-confirm-500': rowStates[item.id] === 'accepted',
                                    'bg-error-500': rowStates[item.id] === 'declined',
                                })}
                            >
                                {rowStates[item.id] === 'accepted' ? (
                                    <CheckCircleRoundedIcon color="white" />
                                ) : (
                                    <CancelRoundedIcon color="white" />
                                )}
                                <Typography color="white.main" fontWeight={500} fontFamily="Roboto">
                                    {rowStates[item.id].toUpperCase()}
                                </Typography>
                            </Card>
                        )
                    ) : (
                        <>
                            <Chip
                                color={item.status === 'accepted' ? 'light-confirm' : 'light-error'}
                                className="w-[90px]"
                                classes={{
                                    label: 'font-semibold text-xs',
                                }}
                                label={item.status?.toUpperCase() ?? ''}
                            />
                        </>
                    )}
                </div>
            ) : (
                <></>
            ),
        [handleAccept, handleDecline, rowStates, tab]
    );

    const handleClose = useCallback(() => {
        setSelectedInstaller(null);
    }, []);

    return (
        <AuthorizedInstallersDashboardContext.Provider value={{ tab, installerRequest: selectedInstaller }}>
            <LoadingContainer isLoading={false}>
                <div className="bg-white h-[66px] flex items-center rounded-md">
                    <Tabs
                        value={tab}
                        onChange={(_e, newValue) => handleParamChange('tab', `${newValue}`)}
                        className="mx-11"
                        slotProps={{
                            indicator: {
                                sx: {
                                    display: 'block',
                                },
                            },
                        }}
                    >
                        <Tab
                            label={
                                <Typography color="black.main">
                                    New Forms{' '}
                                    <Typography component="span" className="ml-2 opacity-40">
                                        {newForms?.count} pending
                                    </Typography>
                                </Typography>
                            }
                        />
                        <Tab label={<Typography color="black.main">Old Forms</Typography>} />
                    </Tabs>
                </div>
                <div className="bg-white rounded-md mt-4 px-11 py-5">
                    <Search
                        className="mb-2"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Divider />
                    <Table
                        emptyListTxt="No pending forms"
                        headers={TABLE_HEADERS}
                        sort={sort}
                        onSortClick={handleSortClick}
                        data={data?.results ?? []}
                        renderRow={renderRow}
                        count={data?.count ?? 0}
                        bottomContainerProps={{
                            className: 'py-4',
                        }}
                        className="min-w-[1200px]"
                        page={Number(filters.page)}
                        onPageChange={handlePageChange}
                        rowsPerPage={Number(filters.page_size)}
                        rowsPerPageOptions={rowsPerPageOptions}
                        setRowsPerPage={handlePageSizeChange}
                        separator="gray"
                        rowOptions={{
                            data: getRowOptions,
                            title: tab === AUTHORIZED_INSTALLER_TABS.NEW_FORMS ? 'ACTIONS' : 'STATUS',
                            tooltip:
                                tab === AUTHORIZED_INSTALLER_TABS.NEW_FORMS
                                    ? 'To confirm or decline, please hold the respective action button'
                                    : '',
                        }}
                        isLoading={isFetching}
                        onRowClick={(row) => setSelectedInstaller(row as IAuthorizedInstallerRequest)}
                    />
                </div>
                <AuthorizedInstallerDialog open={!!selectedInstaller} onClose={handleClose} />
            </LoadingContainer>
        </AuthorizedInstallersDashboardContext.Provider>
    );
};

export { FormsOverview };
